import {
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { Client } from "types/interfaces/adminUsers";
import { GetClientsParams } from "features/adminUsers/adminUsers";
import { ReactNode } from "react";
import CustomScrollbars from "../CustomScrollbars";
import {
  ActionsEditButton,
  actionsColSx,
  UM_TABLE_MIN_WIDTH,
} from "./tableCommon";

type ClientSortField = NonNullable<GetClientsParams["sort_by"]>;

interface ClientsTableProps {
  clients: Client[];
  isLoading: boolean;
  isFetching: boolean;
  selectedClientId?: number;
  onEdit: (client: Client) => void;
  sortBy?: GetClientsParams["sort_by"];
  sortOrder?: "asc" | "desc";
  onSortChange?: (field: ClientSortField) => void;
  pagination?: ReactNode;
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: 7 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton variant="text" width={j === 0 ? 160 : "70%"} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function StatusChip({ client }: { client: Client }) {
  if (client.is_archived) {
    return (
      <Box display="flex" flexDirection="column" gap={0.25}>
        <Chip
          label="ARCHIVED"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.5,
            bgcolor: "rgba(211,47,47,0.10)",
            color: "error.dark",
            alignSelf: "flex-start",
          }}
        />
        {client.archived_at && (
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ fontSize: 10 }}
          >
            {new Date(client.archived_at)
              .toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              })
              .replace(/\//g, ".")}
          </Typography>
        )}
      </Box>
    );
  }
  if (client.scheduled_archive_date) {
    return (
      <Box display="flex" flexDirection="column" gap={0.25}>
        <Chip
          label="SCHEDULED"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.5,
            bgcolor: "rgba(255,152,0,0.12)",
            color: "warning.dark",
            alignSelf: "flex-start",
          }}
        />
        <Typography
          variant="caption"
          color="text.disabled"
          sx={{ fontSize: 10 }}
        >
          {new Date(client.scheduled_archive_date)
            .toLocaleDateString("en-US", {
              month: "2-digit",
              day: "2-digit",
              year: "numeric",
            })
            .replace(/\//g, ".")}
        </Typography>
      </Box>
    );
  }
  return (
    <Chip
      label="ACTIVE"
      size="small"
      sx={{
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: 0.5,
        bgcolor: "rgba(46,125,50,0.12)",
        color: "success.dark",
      }}
    />
  );
}

export default function ClientsTable({
  clients,
  isLoading,
  isFetching,
  selectedClientId,
  onEdit,
  sortBy,
  sortOrder,
  onSortChange,
  pagination,
}: ClientsTableProps) {
  const handleSort = (field: ClientSortField) => {
    onSortChange?.(field);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }} trackOffsetTop={40}>
        <Table stickyHeader size="small" sx={{ minWidth: UM_TABLE_MIN_WIDTH }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                {onSortChange ? (
                  <TableSortLabel
                    active={sortBy === "company_name"}
                    direction={
                      sortBy === "company_name" ? (sortOrder ?? "asc") : "asc"
                    }
                    onClick={() => handleSort("company_name")}
                  >
                    Company Name
                  </TableSortLabel>
                ) : (
                  "Company Name"
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {onSortChange ? (
                  <TableSortLabel
                    active={sortBy === "company_code"}
                    direction={
                      sortBy === "company_code" ? (sortOrder ?? "asc") : "asc"
                    }
                    onClick={() => handleSort("company_code")}
                  >
                    Company Code
                  </TableSortLabel>
                ) : (
                  "Company Code"
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {onSortChange ? (
                  <TableSortLabel
                    active={sortBy === "owner"}
                    direction={
                      sortBy === "owner" ? (sortOrder ?? "asc") : "asc"
                    }
                    onClick={() => handleSort("owner")}
                  >
                    Calibrate Owner
                  </TableSortLabel>
                ) : (
                  "Calibrate Owner"
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {onSortChange ? (
                  <TableSortLabel
                    active={sortBy === "users"}
                    direction={
                      sortBy === "users" ? (sortOrder ?? "asc") : "asc"
                    }
                    onClick={() => handleSort("users")}
                  >
                    Users
                  </TableSortLabel>
                ) : (
                  "Users"
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {onSortChange ? (
                  <TableSortLabel
                    active={sortBy === "created_at"}
                    direction={
                      sortBy === "created_at" ? (sortOrder ?? "asc") : "asc"
                    }
                    onClick={() => handleSort("created_at")}
                  >
                    Created
                  </TableSortLabel>
                ) : (
                  "Created"
                )}
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                {onSortChange ? (
                  <TableSortLabel
                    active={sortBy === "status"}
                    direction={
                      sortBy === "status" ? (sortOrder ?? "asc") : "asc"
                    }
                    onClick={() => handleSort("status")}
                  >
                    Status
                  </TableSortLabel>
                ) : (
                  "Status"
                )}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, ...actionsColSx }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    No clients found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow
                  key={client.id}
                  selected={client.id === selectedClientId}
                  sx={{
                    opacity: isFetching ? 0.6 : 1,
                    "&.Mui-selected": { bgcolor: "calibrate.primarySoft06" },
                    "&.Mui-selected:hover": {
                      bgcolor: "calibrate.primarySoft10",
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {client.company_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontFamily="monospace"
                      fontWeight={600}
                      color="text.secondary"
                    >
                      {client.company_code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {client.calibrate_owners.length === 0 ? (
                      <Typography variant="body2" color="text.disabled">
                        —
                      </Typography>
                    ) : client.calibrate_owners.length === 1 ? (
                      <Typography variant="body2">
                        {client.calibrate_owners[0].first_name ||
                        client.calibrate_owners[0].last_name
                          ? `${client.calibrate_owners[0].first_name} ${client.calibrate_owners[0].last_name}`.trim()
                          : client.calibrate_owners[0].username}
                      </Typography>
                    ) : (
                      <Chip
                        label={`Multiple (${client.calibrate_owners.length})`}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{client.user_count}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(client.created_at).toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip client={client} />
                  </TableCell>
                  <TableCell align="right" sx={actionsColSx}>
                    <ActionsEditButton onClick={() => onEdit(client)} />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination && (
        <Box
          sx={{ flexShrink: 0, borderTop: "1px solid", borderColor: "divider" }}
        >
          {pagination}
        </Box>
      )}
    </Box>
  );
}
